import React from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

import { TVehicle } from "types";
import { VehicleStore, FilterStore, ConfigurationStore } from "stores";

import Card from "./Card";
import Header from "./Header";
import { EmptySearch } from "../";

import { useSmartNavigate } from "helper/SmartLink";

const pageSize = 60;

const Fahrzeuge: React.FC = (): JSX.Element | null => {
  const [page, setPage] = React.useState<number>(1);
  const { state: vehicles } = React.useContext(VehicleStore);

  const configuration = React.useContext(ConfigurationStore);
  const debug = !!configuration.application.debug;

  const {
    state: {
      order: sortOrder,
      filter: { finance: financeFilter },
    },
  } = React.useContext(FilterStore);

  const navigate = useSmartNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    setPage(1);
    window.scrollTo(0, 0);
  }, [sortOrder, vehicles]);

  if (!vehicles) return null;
  if (vehicles.length === 0) return <EmptySearch />;
  const favouritesMarke = configuration?.filter?.favourites?.marke || undefined;
  const favouritesModell =
    configuration?.filter?.favourites?.modell || undefined;

  const sorter = {
    empfohlen: (a: TVehicle, b: TVehicle) => {
      if (favouritesModell) {
        if (favouritesModell.includes(a.modell.id)) {
          if (favouritesModell.includes(b.modell.id)) {
            return b.preis > a.preis ? -1 : 1;
          }
          return -1;
        }
        if (favouritesModell.includes(b.modell.id)) {
          return 1;
        }
      }
      if (favouritesMarke) {
        if (favouritesMarke.includes(a.modell?.marke.id)) {
          if (favouritesMarke.includes(b.modell?.marke.id)) {
            return b.preis > a.preis ? -1 : 1;
          }
          return -1;
        }
        if (favouritesMarke.includes(b.modell?.marke.id)) {
          return 1;
        }
      }
      return b.preis > a.preis ? -1 : 1;
    },

    preis_asc: (a: TVehicle, b: TVehicle) => {
      return b.preis > a.preis ? -1 : 1;
    },
    preis_desc: (a: TVehicle, b: TVehicle) => {
      return b.preis < a.preis ? -1 : 1;
    },
    rate_asc: (a: TVehicle, b: TVehicle) => {
      if (!a.rate) return 1;
      if (!b.rate) return -1;
      return b.rate > a.rate ? -1 : 1;
    },
    rate_desc: (a: TVehicle, b: TVehicle) => {
      if (!a.rate) return 1;
      if (!b.rate) return -1;
      return b.rate < a.rate ? -1 : 1;
    },
    km_asc: (a: TVehicle, b: TVehicle) => {
      if (!a.km) return 1;
      if (!b.km) return -1;
      return b.km > a.km ? -1 : 1;
    },
    km_desc: (a: TVehicle, b: TVehicle) => {
      if (!a.km) return 1;
      if (!b.km) return -1;
      return b.km < a.km ? -1 : 1;
    },
    erstzulassung_asc: (a: TVehicle, b: TVehicle) => {
      if (!a.erstzulassung) return 1;
      if (!b.erstzulassung) return -1;
      return b.erstzulassung > a.erstzulassung ? -1 : 1;
    },
    erstzulassung_desc: (a: TVehicle, b: TVehicle) => {
      if (!a.erstzulassung) return 1;
      if (!b.erstzulassung) return -1;
      return b.erstzulassung < a.erstzulassung ? -1 : 1;
    },
  };

  const sortedvehicles = vehicles.sort(sorter[sortOrder]);
  const vatExcluded = financeFilter?.customerType === "business" || false;

  return (
    <>
      <Header />
      <Box
        sx={{
          m: 0.5,
          display: "grid",
          columnGap: { xs: 1, md: 2, lg: 3 },
          rowGap: { xs: 2, lg: 3 },
          gridTemplateColumns: {
            md: "repeat(3, 1fr)",
            sm: "repeat(4, 1fr)",
            xs: "repeat(2, 1fr)",
          },
        }}
      >
        {sortedvehicles
          .slice(0, page * pageSize)
          .map((vehicle: TVehicle, i: number) => {
            return (
              <Card
                key={vehicle.wgnr}
                vehicle={vehicle}
                onClickHandler={() => navigate("/catalog/dt/" + vehicle.wgnr)}
                debug={debug}
                vatExcluded={vatExcluded}
              />
            );
          })}
      </Box>
      {page * pageSize < vehicles.length && (
        <Divider sx={{ my: 4 }}>
          <Chip
            label="mehr laden"
            size="medium"
            variant="outlined"
            onClick={() => setPage(page + 1)}
            icon={<KeyboardDoubleArrowDownIcon />}
          />
        </Divider>
      )}
    </>
  );
};

export default Fahrzeuge;
